// eslint-disable-next-line eslint-comments/disable-enable-pair
/* eslint-disable formatjs/no-literal-string-in-jsx */
import { PageLayout } from "#/features/global-ui/page-layout"
import { trpc } from "#/lib/trpc/client"
import { Title } from "#/services/page-title/Title"
import { Paths } from "#/utils/Paths"
import { Alert, Progress, Typography } from "@carrotfertility/carotene-core"
import { HealthieAnswer, IntakeForm, useTelemedicineAccess } from "@internal/telemedicine"
import { useMutation, useQuery } from "@tanstack/react-query"
import React from "react"
import { Redirect, useParams } from "react-router-dom"

// --- PoC (MEM-684): hardcoded form + user to prove the FormField renderer ---
// end-to-end against real Healthie data. The `:id` route param is the (future)
// appointment id; appointment→form resolution is a later slice (MEM-686).
const POC_FORM_ID = "2355879" // Men's Health Test Pre-visit Intake (Healthie staging)
const POC_USER_ID = "6168865" // Healthie staging user "Pat POC"

export function TelemedicineBookingFormsPage() {
  const { id } = useParams<{ id: string }>()
  const { data: access, isLoading, isError } = useTelemedicineAccess()
  const hasAccess = access != null && access.eligible && access.entry.status !== "single_unqualified"

  const intakeForm = useQuery({
    queryKey: ["telemedicine", "getForm", POC_FORM_ID],
    queryFn: () => trpc.telemedicine.getForm.query({ formId: POC_FORM_ID }),
    // Don't fetch when the member has no telemedicine access — the component redirects home below.
    enabled: hasAccess
  })

  const submit = useMutation({
    mutationFn: (answers: HealthieAnswer[]) =>
      trpc.telemedicine.submitForm.mutate({ formId: POC_FORM_ID, userId: POC_USER_ID, answers })
  })

  // A failed access read is anomalous at this entry point — send the member home rather than
  // render the PoC page.
  if (isError) {
    return <Redirect to={Paths.HOME} />
  }

  if (isLoading || !access) {
    return null
  }

  if (!hasAccess) {
    return <Redirect to={Paths.HOME} />
  }

  return (
    <PageLayout variant="default">
      <Title title="Telemedicine Intake Form" />

      <Alert severity="info">
        PoC: booking #{id} — rendering hardcoded form #{POC_FORM_ID} for user Pat POC #{POC_USER_ID}
      </Alert>

      {intakeForm.isLoading ? (
        <Progress />
      ) : intakeForm.error ? (
        <Alert severity="error">
          Couldn&apos;t load the form: {intakeForm.error instanceof Error ? intakeForm.error.message : "unknown error"}
        </Alert>
      ) : intakeForm.data ? (
        <IntakeForm
          form={intakeForm.data}
          onSubmit={async (answers) => {
            await submit.mutateAsync(answers)
          }}
        />
      ) : (
        <Typography>Form #{POC_FORM_ID} not found.</Typography>
      )}

      {submit.isSuccess ? (
        <Alert severity="success">Submitted — Healthie form_answer_group #{submit.data.id}</Alert>
      ) : submit.error ? (
        <Alert severity="error">
          Submit failed: {submit.error instanceof Error ? submit.error.message : "unknown error"}
        </Alert>
      ) : null}
    </PageLayout>
  )
}
